import { useState, type JSX } from 'react'
import { useTranslation } from 'react-i18next'

import {
  EmptyTransactionsPageTable,
  IBRebatesTransactionsTable,
} from '@pages/IntroducingBroker/Rebates/Transactions/IBRebatesTransactionsTable'

import { PageSection } from '@ui/components/PageSection'
import { Pagination } from '@ui/v2/Pagination/Pagination'
import { FilterOptions, TableFilter } from '@ui/v2/TableFilter/TableFilter'
import { TableSummary } from '@ui/v2/TableSummary/TableSummary'

import { NameDto } from '@model/NameDto'
import { TransactionStatus } from '@model/TransactionStatus'

import { ScrollToIds } from '@hooks/useScrollToElementIds'

import { Loading } from '@shared/components/Loading/Loading'

import { useIBRebateTransactions } from './hooks/useIBRebateTransactions'
import { useIBTradingAccountsRebateSearch } from './hooks/useIBTradingAccountsRebateSearch'
import { useIBTransactionsFilterOptions } from './hooks/useIBTransactionsFiltersOptions'
import { useIBRebateTransactionsSummary } from './hooks/useIBTransactionsSummary'

import styles from './Transactions.page.module.scss'

export const IBRebatesTransactionsPage = (): JSX.Element => {
  const { t } = useTranslation()
  const [isFilterVisible, setIsFilterVisible] = useState(false)

  const {
    query: tableQuery,
    sortOptions: tableSortOptions,
    search: tableSearch,
    setSearch: setTableSearch,
  } = useIBRebateTransactions()

  const { summaryItems, isLoading: isSummaryLoading } = useIBRebateTransactionsSummary(tableSearch)
  const { accountTypes, dateShortcutOptions } = useIBTransactionsFilterOptions()
  const { tradingAccountOptions, setSearch: setListSearch } = useIBTradingAccountsRebateSearch()

  const isTableLoading = tableQuery.isLoading

  const handlePageChange = (pageIndex: number, pageSize: number) => {
    tableQuery.setPageQuery?.({
      ...tableQuery.pageQuery,
      pageIndex,
      pageSize,
    })
  }

  const transactionStatuses = {
    [TransactionStatus.AwaitingApproval]: t('IB.RebatesPage.Pending'),
    [TransactionStatus.Pending]: t('IB.RebatesPage.Pending'),
    [TransactionStatus.Completed]: t('IB.RebatesPage.Paid'),
    [TransactionStatus.Cancelled]: t('Cancelled'),
    [TransactionStatus.Declined]: t('IB.RebatesPage.Declined'),
    [TransactionStatus.Failed]: t('Failed'),
    [TransactionStatus.Reversed]: t('IB.RebatesPage.Reversed'),
  }

  const rebateTransactionStatusOptions: NameDto<TransactionStatus>[] = [
    {
      id: TransactionStatus.Completed,
      name: transactionStatuses[TransactionStatus.Completed],
    },
    {
      id: TransactionStatus.AwaitingApproval,
      name: transactionStatuses[TransactionStatus.AwaitingApproval],
    },
    {
      id: TransactionStatus.Declined,
      name: transactionStatuses[TransactionStatus.Declined],
    },
    {
      id: TransactionStatus.Reversed,
      name: transactionStatuses[TransactionStatus.Reversed],
    },
  ]

  return (
    <div className={styles.transactionsPage}>
      <TableSummary isLoading={isSummaryLoading} summaryItems={summaryItems} />
      <div className={styles.container}>
        <PageSection
          title={t('IB.RebatesPage.Transactions')}
          id={ScrollToIds.IBRebateTransactionsHeader}
          onFilterClick={() => setIsFilterVisible((prevState) => !prevState)}
        />
        <TableFilter
          isVisible={isFilterVisible}
          searchFilters={tableSearch}
          dateShortcutOptions={dateShortcutOptions}
          statusOptions={rebateTransactionStatusOptions}
          tradingAccountSearchProps={{
            options: tradingAccountOptions,
            setSearch: (search) => search && setListSearch(search),
          }}
          tradingAccountTypeOptions={accountTypes || []}
          filterOptions={[
            FilterOptions.TransactionDate,
            FilterOptions.TradingAccountId,
            FilterOptions.TradingAccountType,
            FilterOptions.AccumulatedVolumeFxm,
            FilterOptions.AccumulatedVolumeOther,
            FilterOptions.AccumulatedCommission,
            FilterOptions.RebateAmount,
            FilterOptions.State,
          ]}
          setSearchFilters={(searchFilters) => {
            if (!searchFilters) {
              return
            }

            setTableSearch(searchFilters)

            tableQuery.setPageQuery?.({
              ...tableQuery.pageQuery,
              pageIndex: 1,
            })
          }}
        />
        <div className={styles.tableContainer}>
          <Loading isLoading={isTableLoading} showLoadingIcon>
            {tableQuery.data.length ? (
              <IBRebatesTransactionsTable
                tableQuery={tableQuery}
                sortOptions={tableSortOptions}
                sticky='left'
                transactionStatuses={transactionStatuses}
              />
            ) : (
              <EmptyTransactionsPageTable />
            )}
            {tableQuery.meta && tableQuery.hasInitialResults && (
              <Pagination
                scrollToHeaderId={ScrollToIds.IBClientsHeader}
                maxPageSize={100}
                pageData={tableQuery.meta}
                isLoading={tableQuery.isLoading}
                onPageChanged={handlePageChange}
              />
            )}
          </Loading>
        </div>
      </div>
    </div>
  )
}
